import React, { CSSProperties, useState } from 'react';

import { TierData } from './useCashierLimits';

type TierExplainerProps = {
  tierInfo: {
    current: TierData | null;
    next: TierData | null;
    all: TierData[];
  };
  handsPlayed: number;
  idVerified: string;
};

const tierColors = ['#cd7f32', '#c0c0c0', '#ffd700', '#e5e4e2', '#63b3ed'];

const formatLimit = (value: number) => `$${Number(value).toLocaleString()}/day`;
const formatHandsMax = (value: number) =>
  value < 0 ? 'Max tier' : `${value.toLocaleString()} hands`;

const formatReq = (tier: TierData) => {
  const parts = [];
  if (tier.handsPlayedMin > 0) parts.push(`${tier.handsPlayedMin.toLocaleString()}+ hands`);
  if (tier.requiredAccountAgeDays && tier.requiredAccountAgeDays > 0) {
    parts.push(`${tier.requiredAccountAgeDays}+ days`);
  }
  return parts.length > 0 ? parts.join(' + ') : 'New account';
};

const TierExplainer = ({ tierInfo, handsPlayed, idVerified }: TierExplainerProps) => {
  const [expanded, setExpanded] = useState(false);
  const isVerified = idVerified === 'APPROVED';
  const current = tierInfo.current;
  const next = tierInfo.next;
  const all = tierInfo.all || [];

  if (!current) return null;

  const color = tierColors[Math.min(current.tier - 1, tierColors.length - 1)];
  const progressMax = current.handsPlayedMax < 0 ? handsPlayed : current.handsPlayedMax;
  const progressPercent =
    current.handsPlayedMax < 0 || progressMax <= 0
      ? 100
      : Math.max(0, Math.min(100, (handsPlayed / progressMax) * 100));
  const handsToNextTier = next ? Math.max(0, next.handsPlayedMin - handsPlayed) : 0;

  return (
    <div style={tierCard}>
      <button onClick={() => setExpanded(!expanded)} style={tierCardBtn}>
        <div style={tierCardHeader}>
          <div style={tierCardTopRow}>
            <div
              style={{
                ...tierBadge,
                backgroundColor: color,
                color: current.tier >= 3 ? '#000' : '#fff'
              }}>
              Tier {current.tier}
            </div>
            <span style={tierCardHands}>
              {handsPlayed.toLocaleString()} / {formatHandsMax(current.handsPlayedMax)}
            </span>
          </div>

          <div style={tierProgressTrack}>
            <div
              style={{ ...tierProgressFill, width: `${progressPercent}%`, backgroundColor: color }}
            />
          </div>

          <span style={tierCardLabel}>
            {next
              ? `${handsToNextTier.toLocaleString()} hands to Tier ${next.tier}`
              : 'Highest tier unlocked'}
          </span>
        </div>

        <span style={tierExpandIcon}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={tierExpandedContent}>
          <span style={tierDesc}>
            Online deposit: {formatLimit(current.onlineDepositLimit)}. Online withdrawal:{' '}
            {formatLimit(current.onlineWithdrawalLimit)}. In-person deposit:{' '}
            {formatLimit(current.inPersonDepositLimit)}. In-person withdrawal:{' '}
            {formatLimit(current.inPersonWithdrawalLimit)}. Card deposit:{' '}
            {formatLimit(current.cardDepositLimit)}. Crypto withdrawal:{' '}
            {formatLimit(current.cryptoWithdrawalLimit)}.
          </span>

          {!isVerified && (
            <div style={tierVerifyNote}>
              <span style={tierVerifyText}>
                Complete ID verification to unlock higher tiers and limits.
              </span>
            </div>
          )}

          {next && (
            <div style={tierNextBox}>
              <span style={tierNextLabel}>Next: Tier {next.tier}</span>
              <span style={tierNextReq}>
                {formatReq(next)} {'->'} online deposit {formatLimit(next.onlineDepositLimit)}
              </span>
            </div>
          )}

          {all.length > 0 && (
            <div style={tierAllTiers}>
              <span style={tierAllLabel}>All Tiers</span>
              {all.map((tier, index) => (
                <div
                  key={tier.tier}
                  style={{ ...tierRow, ...(tier.tier === current.tier ? tierRowActive : {}) }}>
                  <span
                    style={{
                      ...tierRowName,
                      color: tierColors[Math.min(index, tierColors.length - 1)]
                    }}>
                    Tier {tier.tier}
                  </span>
                  <span style={tierRowReq}>{formatReq(tier)}</span>
                  <span style={tierRowLimits}>
                    Online deposit {formatLimit(tier.onlineDepositLimit)} · Online withdrawal{' '}
                    {formatLimit(tier.onlineWithdrawalLimit)}
                  </span>
                  <span style={tierRowLimitsSecondary}>
                    In-person deposit {formatLimit(tier.inPersonDepositLimit)} · In-person
                    withdrawal {formatLimit(tier.inPersonWithdrawalLimit)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TierExplainer;

const tierCard: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '12px',
  overflow: 'hidden'
};

const tierCardBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
  padding: '10px 14px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  gap: '10px'
};

const tierCardHeader: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1
};

const tierCardTopRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px'
};

const tierBadge: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 800,
  padding: '3px 8px',
  borderRadius: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};

const tierCardLabel: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.55)',
  textAlign: 'left'
};

const tierCardHands: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.4)'
};

const tierExpandIcon: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.4)',
  paddingTop: '6px'
};

const tierProgressTrack: CSSProperties = {
  width: '100%',
  height: 6,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 999,
  overflow: 'hidden'
};

const tierProgressFill: CSSProperties = {
  height: '100%',
  borderRadius: 999
};

const tierExpandedContent: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '0 14px 14px',
  borderTop: '1px solid rgba(255,255,255,0.06)'
};

const tierDesc: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.4,
  paddingTop: '10px'
};

const tierVerifyNote: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  backgroundColor: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.15)',
  borderRadius: '8px'
};

const tierVerifyText: CSSProperties = {
  fontSize: '0.7rem',
  color: '#E98C26',
  fontWeight: 600
};

const tierNextBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '8px 10px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  borderRadius: '8px'
};

const tierNextLabel: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.7)'
};

const tierNextReq: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.4)'
};

const tierAllTiers: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
};

const tierAllLabel: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.3)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '4px'
};

const tierRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '8px 10px',
  borderRadius: '6px',
  backgroundColor: 'rgba(255,255,255,0.02)'
};

const tierRowActive: CSSProperties = {
  backgroundColor: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.15)'
};

const tierRowName: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700
};

const tierRowReq: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.4)'
};

const tierRowLimits: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.5)',
  fontWeight: 600
};

const tierRowLimitsSecondary: CSSProperties = {
  fontSize: '0.58rem',
  color: 'rgba(255,255,255,0.38)',
  fontWeight: 600
};
